import React from "react";
import { useTranslation } from "react-i18next";
import { ShoppingCart, ExternalLink } from "lucide-react";

/**
 * iter 86 — Shop deeplink panel (iter 90: aggregated "view all" CTA removed
 * at the user's request — fabricadeprofile.ro search only works with ONE
 * profile code at a time, so the multi-code search URL returned nothing).
 *
 * Renders below the variations grid on a completed simulation. Closes
 * the loop "preview → desire → purchase" by surfacing per-code chips that
 * link straight to the category page on fabricadeprofile.ro with the code
 * as anchor — so the user can see ALL variants of that profile family.
 *
 * All links carry UTM tracking so FDP can attribute conversions to
 * onefdp in their analytics (the backend appends the params).
 */
export default function ShopDeeplinkPanel({ shopLinks }) {
  const { t } = useTranslation();
  if (!shopLinks || !shopLinks.total_codes || !Array.isArray(shopLinks.products)) {
    return null;
  }
  const { products } = shopLinks;

  return (
    <div
      className="mt-6 mb-4 rounded-xl border border-orange-500/20 bg-gradient-to-br from-orange-950/40 via-zinc-950/60 to-zinc-950/80 p-5 shadow-[0_8px_30px_rgb(238_116_64_/_0.08)]"
      data-testid="shop-deeplink-panel"
    >
      <div className="mb-4">
        <div className="flex items-center gap-2 mb-1">
          <ShoppingCart size={18} className="text-orange-400 shrink-0" />
          <h3 className="text-lg font-semibold text-white tracking-tight">
            {t("detail.shop_panel_title")}
          </h3>
        </div>
        <p className="text-sm text-zinc-400">
          {t("detail.shop_panel_subtitle")}
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
        {products.map((p) => (
          <a
            key={p.code}
            href={p.url}
            target="_blank"
            rel="noreferrer noopener"
            className="group flex items-center justify-between gap-2 rounded-lg border border-white/5 bg-zinc-900/50 hover:bg-zinc-800/60 hover:border-orange-500/30 px-3 py-2.5 transition-all"
            data-testid={`shop-deeplink-${p.code}`}
            aria-label={t("detail.shop_panel_open_tab_aria")}
          >
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="font-mono text-orange-400 font-semibold uppercase text-sm group-hover:text-orange-300 transition">
                  CP {p.code}
                </span>
              </div>
              {(p.category_name || p.occurrences > 1) && (
                <div className="text-[11px] text-zinc-500 mt-0.5 truncate">
                  {p.category_name}
                  {p.occurrences > 1 && (
                    <span className="ml-1.5 text-zinc-600">
                      · {t("detail.shop_panel_occurrences", { count: p.occurrences })}
                    </span>
                  )}
                </div>
              )}
            </div>
            <ExternalLink
              size={14}
              className="text-zinc-600 group-hover:text-orange-400 shrink-0 transition"
            />
          </a>
        ))}
      </div>
    </div>
  );
}
